import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { useApi } from "hooks/useApis";
import { useDispatch } from "react-redux";
import { setUserAgent, clearUserAgent } from "store/userSlice";

const AuthContext = createContext({
  user: null,
  cpf: null,
  token: null,
  tokenDashboard: null,
  login: null,
  logout: null,
  expirationWarning: null,
  setExpirationWarning: null,
});
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("cria-web@user", null);
  const [cpf, setCpf] = useLocalStorage("cria-web@user", null);
  const [agent, setAgent] = useLocalStorage("cria-web@agent", null);
  const [userAgentName, setUserAgentName] = useLocalStorage(
    "cria-web@userAgentName",
    null
  );
  const [token, setToken] = useLocalStorage("cria-web@token", null);
  const [refreshToken, setRefreshToken] = useLocalStorage(
    "cria-web@refreshToken",
    null
  );
  const [tokenDashboard, setTokenDashboard] = useLocalStorage(
    "cria-web@token-dashboard",
    null
  );
  const [refreshtokenDashboard, setRefreshTokenDashboard] = useLocalStorage(
    "cria-web@refreshToken-dashboard",
    null
  );
  const [detail, setDetail] = useLocalStorage("cria-web@detail", null);
  const [expirationWarning, setExpirationWarning] = useLocalStorage(
    "cria-web@expirationWarning",
    false
  );
  const [finishApiDashboard, setFinishApiDashboard] = useState(false);
  const api = useApi();
  const dispatch = useDispatch();

  const login = async (cpf, password) => {
    const response = await api.login(cpf, password);
    const data = response.data;

    if (data.access && data.refresh) {
      setCpf(cpf);
      setToken(data.access);
      setRefreshToken(data.refresh);
      setAgent(data.agent);
      setUserAgentName(data.agent?.name);
      dispatch(setUserAgent(data.agent));

      return { isLogged: true, response };
    }

    const errorMessage = data?.detail || "Erro desconhecido no login.";
    throw { response: { data: { detail: errorMessage } } };
  };

  const loginDashboard = async (email, password, cpf) => {
    setFinishApiDashboard(false);
    const response = await api.loginDashboard(email, password);

    if (response.status === 401) {
      const responseSignUp = await api.signUpDashboard(email, password, cpf);
      if (responseSignUp.status === 201) {
        loginDashboard(email, password, cpf);
      }
    } else if (response.status === 200) {
      setFinishApiDashboard(true);
      const {
        data: { data },
      } = response;
      const { access_token, refresh_token } = data;
      setTokenDashboard(access_token);
      setRefreshTokenDashboard(refresh_token)
    }
  };


  const refreshAccessToken = async () => {
    if (!refreshToken) return false;

    try {
      const response = await api.refreshToken(refreshToken);
      if (response?.access) {
        setToken(response.access);
        return true;
      }
    } catch (error) {
      console.error("Erro ao renovar o token:", error);
    }

    return false;
  };

  const checkToken = async () => {
    try {
      await api.getCadVersion();
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        return await refreshAccessToken();
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error("Erro ao deslogar:", err);
    } finally {
      sessionStorage.removeItem("cria-web@agentFilters");
      sessionStorage.removeItem("cria-web@beneficiaryFilters");
      setCpf(null);
      setUserAgentName(null);
      setUser(null);
      setAgent(null);
      setToken(null);
      setRefreshToken(null);
      setTokenDashboard(null);
      setRefreshTokenDashboard(null);
      setDetail(null);
      setExpirationWarning(false);
      dispatch(clearUserAgent());
      window.location.href = "/";
    }
  };

  useEffect(() => {
    if (refreshToken) refreshAccessToken();
  }, [refreshToken]);

  const value = useMemo(
    () => ({
      user,
      token,
      cpf,
      agent,
      detail,
      tokenDashboard,
      login,
      loginDashboard,
      logout,
      checkToken,
      userAgentName,
      expirationWarning,
      setExpirationWarning,
    }),
    [user, token, cpf, agent, tokenDashboard, detail, expirationWarning]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
